import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'

import { theme } from 'styles/theme'

type MobileCardSkeletonProps = {
  /** Quantos cards desenhar. */
  cards?: number
  /** Quantas linhas rótulo/valor cada card mostra. */
  rowsPerCard?: number
  /** Quantos ícones o rodapé de ações desenha. */
  actionsCount?: number
  /** Esconde o rodapé de ações — para tabelas sem coluna de ações. */
  hideActions?: boolean
  /** Esconde o bloco de paginação — para listas com `hiddePagination`. */
  hidePagination?: boolean
}

/**
 * Espelho do <MobileCardList> enquanto os dados carregam. Mantém a mesma
 * altura e o mesmo ritmo do card real para a lista não "pular" na troca.
 */
export const MobileCardSkeleton = ({
  cards = 4,
  rowsPerCard = 3,
  actionsCount = 5,
  hideActions = false,
  hidePagination = false
}: MobileCardSkeletonProps) => (
  <Box>
    <Box display="flex" flexDirection="column" gap="12px">
      {Array.from({ length: cards }).map((_, cardIndex) => (
        <Box
          key={`card-${cardIndex}`}
          sx={{
            border: `1px solid ${theme.palette.neutral[700]}`,
            borderRadius: '12px',
            padding: '12px 14px'
          }}
        >
          <Box display="flex" alignItems="center" gap="8px">
            <Skeleton
              variant="circular"
              width={28}
              height={28}
              animation="wave"
              sx={{ bgcolor: theme.palette.neutral[700], flexShrink: 0 }}
            />
            <Skeleton
              variant="text"
              width="45%"
              height={18}
              animation="wave"
              sx={{ bgcolor: theme.palette.neutral[700] }}
            />
            <Box flex={1} />
            <Skeleton
              variant="rounded"
              width={70}
              height={22}
              animation="wave"
              sx={{
                bgcolor: theme.palette.neutral[700],
                borderRadius: '9999px',
                flexShrink: 0
              }}
            />
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            rowGap="8px"
            marginTop="12px"
          >
            {Array.from({ length: rowsPerCard }).map((_, rowIndex) => (
              <Box
                key={`row-${cardIndex}-${rowIndex}`}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap="16px"
              >
                <Skeleton
                  variant="text"
                  width="30%"
                  height={16}
                  animation="wave"
                  sx={{ bgcolor: theme.palette.neutral[800] }}
                />
                <Skeleton
                  variant="text"
                  width="35%"
                  height={16}
                  animation="wave"
                  sx={{ bgcolor: theme.palette.neutral[700] }}
                />
              </Box>
            ))}
          </Box>

          {!hideActions && (
            <Box
              marginTop="12px"
              paddingTop="8px"
              borderTop={`1px solid ${theme.palette.neutral[700]}`}
              display="flex"
              justifyContent="center"
              gap="16px"
            >
              {Array.from({ length: actionsCount }).map((_, iconIndex) => (
                <Skeleton
                  key={`action-${cardIndex}-${iconIndex}`}
                  variant="circular"
                  width={24}
                  height={24}
                  animation="wave"
                  sx={{ bgcolor: theme.palette.neutral[800] }}
                />
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Box>

    {!hidePagination && (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap="12px"
        marginTop="16px"
      >
        <Skeleton
          variant="circular"
          width={28}
          height={28}
          animation="wave"
          sx={{ bgcolor: theme.palette.neutral[800] }}
        />
        <Skeleton
          variant="text"
          width={90}
          height={18}
          animation="wave"
          sx={{ bgcolor: theme.palette.neutral[700] }}
        />
        <Skeleton
          variant="circular"
          width={28}
          height={28}
          animation="wave"
          sx={{ bgcolor: theme.palette.neutral[800] }}
        />
      </Box>
    )}
  </Box>
)

export default MobileCardSkeleton
